import type {
  BankReconciliationStatus,
  FinancialTransactionStatus,
  MoneyValue,
  ReconciliationStatus,
} from '@mfos/shared';
import { NavLink } from 'react-router';
import { Badge } from '../../components/Badge';
import { useI18n } from '../../lib/i18n/runtime';
import {
  MOVEMENT_STATUS,
  RECONCILIATION_STATUS,
  TRANSACTION_STATUS,
  formatMoney,
  isNegativeAmount,
} from './finance-display';

/** Negative amounts carry a visible "(negative)" cue for screen readers, not only a minus sign. */
export function MoneyText({ money }: { money: MoneyValue }) {
  const negative = isNegativeAmount(money.amount);
  return (
    <span className="numeric">
      {formatMoney(money)}
      {negative && <span className="visually-hidden"> (negative)</span>}
    </span>
  );
}

export function TransactionStatusBadge({ status }: { status: FinancialTransactionStatus }) {
  const display = TRANSACTION_STATUS[status];
  return <Badge tone={display.tone} label={display.label} />;
}

export function ReconciliationBadge({ status }: { status: ReconciliationStatus }) {
  const display = RECONCILIATION_STATUS[status];
  return <Badge tone={display.tone} label={display.label} />;
}

export function MovementStatusBadge({ status }: { status: BankReconciliationStatus }) {
  const display = MOVEMENT_STATUS[status];
  return <Badge tone={display.tone} label={display.label} />;
}

const SECTIONS = [
  { path: '', label: 'overview', end: true },
  { path: 'transactions', label: 'incomeExpenses', end: false },
  { path: 'pnl', label: 'monthlyPnl', end: false },
  { path: 'cash', label: 'cashFx', end: false },
  { path: 'reconciliation', label: 'reconciliation', end: false },
  { path: 'funding', label: 'founderFunding', end: false },
  { path: 'distributions', label: 'reserveDistributions', end: false },
  { path: 'valuation', label: 'valuation', end: false },
] as const;

export function FinanceNav({ base }: { base: string }) {
  const { t } = useI18n();
  return (
    <nav aria-label={t('financeSections')} className="tabs">
      <ul>
        {SECTIONS.map((section) => (
          <li key={section.path}>
            <NavLink
              to={section.path === '' ? `${base}/finance` : `${base}/finance/${section.path}`}
              end={section.end}
            >
              {t(section.label)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
